import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNil, range} from 'ramda';

import {SalesLeagueLeaderboardResponse, SalesLeagueRow} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeaguePodium} from '../../../components/SalesLeaguePodium';
import {
  SalesLeaguePodiumEntry,
  SalesLeaguePodiumStat,
} from '../../../components/SalesLeaguePodiumCard';
import {
  SALES_LEAGUE_PODIUM_PLACES,
  SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE,
  SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX,
  SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE,
} from '../../../constants/SALES_LEAGUE_PODIUM';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {getSalesLeagueRepName} from '../utils/getSalesLeagueRepName';
import {SalesLeagueAvatar} from './SalesLeagueAvatar';

interface SalesLeagueBoardPodiumProps extends RequiredTestIdProps {
  leaderboard: SalesLeagueLeaderboardResponse;
  periodLabel: string;
}

/**
 * The top three by rank. Only reps who sold reach this board, so a place is unfilled
 * exactly when the board is shorter than the podium. Margin stats drop out with the
 * permission rather than dashing out, matching the tiles and the table columns.
 */
export function SalesLeagueBoardPodium(props: SalesLeagueBoardPodiumProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const vehiclesLabel = i18n.t('page.salesLeague.podium.columnVehicles');
  const marginStatLabels = [
    i18n.t('page.salesLeague.table.columnMarginPerVehicle'),
    i18n.t('page.salesLeague.table.columnTotalMargin'),
  ];

  const statLabels = props.leaderboard.marginVisible
    ? [vehiclesLabel, ...marginStatLabels]
    : [vehiclesLabel];

  const buildEntry = (
    row: SalesLeagueRow | Nullish,
    place: number
  ): SalesLeaguePodiumEntry | Nullish => {
    if (isNil(row)) {
      return undefined;
    }

    const repName = getSalesLeagueRepName(row);
    const vehiclesSold = formatNumber(row.vehiclesSold) ?? EMPTY_PLACEHOLDER;

    const marginStats: SalesLeaguePodiumStat[] = props.leaderboard.marginVisible
      ? [
          {
            label: i18n.t('page.salesLeague.table.columnMarginPerVehicle'),
            value: formatMarginAmount(
              row.marginPerVehicle,
              props.leaderboard.currency,
              formatCurrency
            ),
          },
          {
            label: i18n.t('page.salesLeague.table.columnTotalMargin'),
            value: formatMarginAmount(row.totalMargin, props.leaderboard.currency, formatCurrency),
          },
        ]
      : [];

    return {
      leadingVisual: (
        <SalesLeagueAvatar
          name={repName}
          userId={row.userId}
          size={
            place === SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX
              ? SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE
              : SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE
          }
          data-testid={suffixTestId('podiumRep', props)}
        />
      ),
      title: repName,
      subtitle: i18n.t('page.salesLeague.podium.vehiclesCount', {count: vehiclesSold}),
      stats: [{label: vehiclesLabel, value: vehiclesSold}, ...marginStats],
    };
  };

  return (
    <SalesLeaguePodium
      entries={range(0, SALES_LEAGUE_PODIUM_PLACES).map((place) =>
        buildEntry(props.leaderboard.rows[place], place)
      )}
      periodLabel={props.periodLabel}
      unfilledSubtitle={i18n.t('page.salesLeague.podium.unfilledRep')}
      unfilledStatLabels={statLabels}
      data-testid={props['data-testid']}
    />
  );
}
